import { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import {
  ArrowLeftIcon, DocumentTextIcon, ArrowUpTrayIcon,
  CheckCircleIcon, XCircleIcon, ClockIcon, PaperClipIcon,
} from '@heroicons/react/24/outline';
import api from '../../api/client';
import { DATE_FLOOR } from '../../lib/dateBounds';

// Staff-side: submit a doctor's note for a specific date, view the
// status of your prior submissions. Director sees the mirror view
// at /admin/doctor-notes to approve/deny.

const STATUS_META = {
  pending:  { label: 'Pending review', cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: ClockIcon },
  approved: { label: 'Approved',       cls: 'bg-green-50 text-green-700 border-green-200', Icon: CheckCircleIcon },
  denied:   { label: 'Denied',         cls: 'bg-red-50 text-red-700 border-red-200',     Icon: XCircleIcon },
};

export default function MyDoctorNotesPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/doctor-notes')
      .then(({ data }) => setRequests(data))
      .catch(() => toast.error('Could not load your requests'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <Toaster position="top-right" />
      <div className="max-w-3xl mx-auto">
        <Link to="/my" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeftIcon className="w-4 h-4" /> Back
        </Link>
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Doctor's Notes</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Upload a doctor's note to request a points adjustment for a call-off or late arrival.
              Director reviews and decides.
            </p>
          </div>
          <button onClick={() => setShowForm(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2 rounded-xl inline-flex items-center gap-1">
            <ArrowUpTrayIcon className="w-4 h-4" /> Submit Note
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" /></div>
        ) : requests.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center">
            <DocumentTextIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-600">No doctor's note requests yet.</p>
            <p className="text-xs text-gray-500 mt-1">Submit one above if you have a doctor's note for a recent absence.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {requests.map(r => (
              <RequestCard key={r.id} r={r} />
            ))}
          </div>
        )}
      </div>

      {showForm && <SubmitModal onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function RequestCard({ r }) {
  const meta = STATUS_META[r.status] || STATUS_META.pending;
  const Icon = meta.Icon;
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">For {(() => {
              const [y, m, d] = String(r.event_date).slice(0, 10).split('-').map(Number);
              return new Date(y, m - 1, d).toLocaleDateString();
            })()}</span>
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${meta.cls}`}>
              <Icon className="w-3 h-3" />{meta.label}
            </span>
          </div>
          {r.staff_message && <p className="text-xs text-gray-500 mt-1">Your note: {r.staff_message}</p>}
          {r.status === 'approved' && (
            <p className="text-xs text-green-700 mt-1 font-medium">
              +{r.points_adjustment} points refunded. {r.decision_notes && <>· {r.decision_notes}</>}
            </p>
          )}
          {r.status === 'denied' && r.decision_notes && (
            <p className="text-xs text-red-700 mt-1">Reason: {r.decision_notes}</p>
          )}
          <p className="text-[11px] text-gray-400 mt-1">
            Submitted {new Date(r.requested_at).toLocaleString()}
          </p>
        </div>
        <a href={r.doc_url} target="_blank" rel="noreferrer"
          className="text-xs font-medium text-orange-600 hover:text-orange-700 border border-orange-200 hover:border-orange-300 rounded-lg px-3 py-1.5 inline-flex items-center gap-1 flex-shrink-0">
          <PaperClipIcon className="w-3.5 h-3.5" /> View note
        </a>
      </div>
    </div>
  );
}

function SubmitModal({ onClose, onDone }) {
  const [eventDate, setEventDate] = useState(new Date().toISOString().slice(0, 10));
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [deadlineHours, setDeadlineHours] = useState(72);
  const fileInputRef = useRef();

  // Pull the deadline config so the modal can show a countdown warning
  // when the picked date is close to (or past) the cutoff.
  useEffect(() => {
    api.get('/wallet/config')
      .then(({ data }) => {
        const n = Number(data?.doctor_note_deadline_hours);
        if (Number.isFinite(n) && n > 0) setDeadlineHours(n);
      })
      .catch(() => {}); // fine — default 72 stays
  }, []);

  // Rough client-side deadline check (midnight ET of event_date + N hours).
  // Server does the authoritative check with the actual shift start.
  const deadlineHint = (() => {
    if (!eventDate) return null;
    // Interpret event_date as midnight ET → convert to a UTC instant.
    const dt = new Date(`${eventDate}T04:00:00Z`); // ET midnight ≈ 04:00 UTC (EDT); 05:00 in EST
    const dl = new Date(dt.getTime() + deadlineHours * 60 * 60 * 1000);
    const hoursLeft = (dl.getTime() - Date.now()) / (60 * 60 * 1000);
    if (hoursLeft < 0) {
      return { kind: 'past', text: `Deadline passed. Talk to your director — they can still add it on your behalf if there's a good reason.` };
    }
    if (hoursLeft < 24) {
      return { kind: 'warn', text: `Approximately ${Math.max(1, Math.round(hoursLeft))} hours left to submit for this date.` };
    }
    return null;
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Attach the doctor\'s note.');
    if (!eventDate) return toast.error('Pick the date of the absence.');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('document', file);
      fd.append('event_date', eventDate);
      if (message) fd.append('staff_message', message);
      await api.post('/doctor-notes', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Submitted — director will review.');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Submit failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3">
          <h3 className="text-base font-semibold text-gray-900">Submit Doctor's Note</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Attach a photo or PDF and tell the director which date it covers.
          </p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Date of absence</label>
            <input type="date" min={DATE_FLOOR} value={eventDate} onChange={(e) => setEventDate(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
            <p className="text-[11px] text-gray-500 mt-1">
              You have {deadlineHours} hours after your shift starts to submit.
            </p>
            {deadlineHint && (
              <p className={`text-xs mt-1 ${deadlineHint.kind === 'past' ? 'text-red-600' : 'text-amber-700'}`}>
                {deadlineHint.text}
              </p>
            )}
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Doctor's note</label>
            <button type="button" onClick={() => fileInputRef.current?.click()}
              className="w-full text-left rounded-lg border border-dashed border-gray-300 hover:border-orange-400 px-3 py-2 text-sm text-gray-600">
              {file ? `${file.name} (${(file.size / 1024).toFixed(1)} KB)` : 'Click to attach photo or PDF…'}
            </button>
            <input ref={fileInputRef} type="file" className="sr-only"
              accept=".pdf,.jpg,.jpeg,.png,.heic"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Message (optional)</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)}
              rows={3} placeholder="Any context for the director"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="text-sm px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100">Cancel</button>
            <button type="submit" disabled={busy}
              className="text-sm px-4 py-1.5 rounded-lg bg-orange-500 text-white font-semibold hover:bg-orange-600 disabled:opacity-50">
              {busy ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
